import React, { useEffect, useRef, useState } from "react";
import { AlertCircle, Briefcase, CheckCircle2, FileText, FolderOpen, RefreshCw, Trash2, Upload } from "lucide-react";
import TiltCard from "./TiltCard";
import { hasApi, NO_BACKEND_MESSAGE } from "../lib/api";
import { CAREERS_FOLDER, DRIVE_NOT_CONNECTED, deleteSiteFile, fetchDriveStatus, fetchSiteCareers, uploadSiteFile } from "../lib/siteDrive";

export default function CareersAdmin({ allowEdit }) {
  const [files, setFiles] = useState([]);
  const [drive, setDrive] = useState({ connected: false, error: null });
  const [status, setStatus] = useState("loading");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [form, setForm] = useState({ title: "", last_date: "", summary: "" });
  const [fileName, setFileName] = useState("");
  const inputRef = useRef();

  const load = async () => {
    setStatus("loading");
    try {
      const [listing, info] = await Promise.all([fetchSiteCareers(), fetchDriveStatus()]);
      setFiles(listing.files || []);
      setDrive({
        connected: info.connected || listing.connected,
        error: info.error || listing.error,
      });
      setStatus(listing.error && !listing.connected ? "error" : "ready");
    } catch {
      setDrive({ connected: false, error: DRIVE_NOT_CONNECTED });
      setStatus("error");
    }
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!allowEdit) {
      setMsg({ ok: false, text: "Your account is set to view only for Careers. Ask the administrator for edit access." });
      return;
    }
    if (!hasApi()) { setMsg({ ok: false, text: NO_BACKEND_MESSAGE }); return; }
    if (!form.title.trim()) {
      setMsg({ ok: false, text: "Please enter a job title." });
      return;
    }
    const file = inputRef.current?.files?.[0];
    if (!form.summary.trim() && !file) {
      setMsg({ ok: false, text: "Add a short description or attach a PDF." });
      return;
    }
    setBusy(true);
    setMsg(null);
    try {
      const fd = new FormData();
      fd.append("title", form.title.trim());
      fd.append("last_date", form.last_date.trim());
      fd.append("summary", form.summary.trim());
      if (file) fd.append("file", file);
      const result = await uploadSiteFile("/api/careers/upload", fd);
      if (!result.ok) {
        setMsg({ ok: false, text: result.detail || "Could not publish the job." });
        return;
      }
      setMsg({ ok: true, text: "Job added. It is now on the public Careers page." });
      setForm({ title: "", last_date: "", summary: "" });
      setFileName("");
      if (inputRef.current) inputRef.current.value = "";
      await load();
    } catch (err) {
      setMsg({ ok: false, text: err?.message || "Could not publish the job." });
    } finally {
      setBusy(false);
    }
  };

  const remove = async (f) => {
    if (!allowEdit) return;
    if (!window.confirm(`Remove “${f.title || f.name}” from Careers?`)) return;
    setBusy(true);
    setMsg(null);
    const result = await deleteSiteFile(`/api/careers/${f.id}`);
    setBusy(false);
    if (!result.ok) {
      setMsg({ ok: false, text: result.detail || "Could not remove the job." });
      return;
    }
    setMsg({ ok: true, text: "Removed from the Careers page." });
    load();
  };

  return (
    <TiltCard intensity={2}>
      <div style={{ padding: 26 }} data-testid="careers-admin">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <div style={{ fontSize: 17, fontWeight: 800, color: "#1C1917", display: "flex", alignItems: "center", gap: 8 }}>
            <Briefcase size={20} color="#F97316" /> Careers
          </div>
          <button type="button" onClick={load} className="btn-outline-3d" style={{ padding: "6px 12px", fontSize: 12 }} data-testid="careers-admin-refresh">
            <RefreshCw size={12} /> Refresh
          </button>
        </div>
        <div className="drive-folder-using" data-testid="careers-admin-folder-label">
          <FolderOpen size={14} style={{ verticalAlign: "middle", marginRight: 6 }} />
          Using Drive folder: {CAREERS_FOLDER}
        </div>
        <p style={{ fontSize: 13.5, color: "#57534E", marginTop: 8, lineHeight: 1.6 }}>
          {allowEdit
            ? "Add a job with a title, last date, and a short note or PDF. It is stored in the Careers folder on Drive and appears on the public Careers page."
            : "Your account is set to view only. You can see published jobs. The administrator must grant edit access before you can add or remove them."}
        </p>

        {!allowEdit && (
          <div data-testid="careers-admin-view-only" style={{ marginTop: 12, padding: "12px 14px", background: "#FFF7ED", border: "1px solid rgba(249,115,22,0.25)", borderRadius: 12, color: "#9A3412", fontSize: 13, fontWeight: 600 }}>
            View access — add and delete stay locked.
          </div>
        )}

        {!drive.connected && (
          <div data-testid="careers-admin-disconnected" style={{ marginTop: 12, padding: "12px 14px", background: "#FEF2F2", border: "1px solid rgba(220,38,38,0.3)", borderRadius: 12, color: "#B91C1C", fontSize: 13, display: "flex", gap: 8 }}>
            <AlertCircle size={16} style={{ flexShrink: 0 }} />
            {drive.error || DRIVE_NOT_CONNECTED}. Jobs cannot be published until Drive is connected on Render.
          </div>
        )}

        {allowEdit && (
          <form onSubmit={submit} data-testid="careers-admin-form" style={{ marginTop: 16, display: "grid", gap: 10 }}>
            <input className="input-3d" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Job title" data-testid="careers-admin-title" />
            <input className="input-3d" type="date" value={form.last_date} onChange={(e) => setForm({ ...form, last_date: e.target.value })} data-testid="careers-admin-last-date" />
            <textarea className="input-3d" rows={3} value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} placeholder="Short description (or attach a PDF below)" data-testid="careers-admin-summary" style={{ resize: "vertical" }} />
            <label className="btn-outline-3d" style={{ cursor: "pointer", justifyContent: "center" }} data-testid="careers-admin-file-label">
              <Upload size={14} /> {fileName || "Optional PDF (max 25 MB)"}
              <input ref={inputRef} type="file" accept="application/pdf,.pdf" data-testid="careers-admin-file" style={{ display: "none" }} onChange={(e) => setFileName(e.target.files?.[0]?.name || "")} />
            </label>
            <button type="submit" className="btn-primary-3d" disabled={busy || !drive.connected} data-testid="careers-admin-submit" style={{ width: "100%" }}>
              {busy ? "Publishing…" : "Publish job"}
            </button>
          </form>
        )}

        {msg && (
          <div data-testid="careers-admin-msg" style={{ marginTop: 12, fontSize: 13.5, fontWeight: 600, color: msg.ok ? "#15803D" : "#B91C1C", display: "flex", gap: 6 }}>
            {msg.ok ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />} {msg.text}
          </div>
        )}

        <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: 1, color: "#EA580C", marginTop: 22 }}>
          FILES IN {CAREERS_FOLDER.toUpperCase()} ({status === "loading" ? "…" : files.length})
        </div>
        {status === "loading" && (
          <div data-testid="careers-admin-loading" style={{ marginTop: 10, fontSize: 13, color: "#78716C" }}>Loading careers…</div>
        )}
        {status === "error" && !files.length && (
          <div data-testid="careers-admin-error" style={{ marginTop: 10, fontSize: 13, color: "#B91C1C" }}>{drive.error || DRIVE_NOT_CONNECTED}</div>
        )}
        <div style={{ display: "grid", gap: 10, marginTop: 10 }} data-testid="careers-admin-list">
          {status !== "loading" && files.map((f) => (
            <div key={f.id} data-testid={`careers-admin-item-${f.id}`} style={{ borderRadius: 12, border: "1px solid rgba(249,115,22,0.25)", background: "#fff", padding: 14 }}>
              <div style={{ fontWeight: 800, color: "#1C1917" }}>{f.title || f.name}</div>
              <div style={{ fontSize: 12.5, color: "#78716C", marginTop: 4 }}>
                {f.last_date ? `Last date: ${f.last_date}` : "No last date"} · {f.date || ""}
              </div>
              {f.summary && <div style={{ fontSize: 13, color: "#44403C", marginTop: 8, lineHeight: 1.5 }}>{f.summary}</div>}
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
                {(f.view || f.download) && (
                  <a href={f.view || f.download} target="_blank" rel="noreferrer" className="btn-outline-3d" style={{ padding: "7px 10px", fontSize: 12, textDecoration: "none" }}>
                    <FileText size={12} /> {f.has_file ? "Open PDF" : "Open"}
                  </a>
                )}
                {allowEdit && (
                  <button type="button" className="btn-outline-3d" data-testid={`careers-admin-delete-${f.id}`} onClick={() => remove(f)} disabled={busy || !drive.connected} style={{ padding: "7px 10px", fontSize: 12, color: "#B91C1C" }}>
                    <Trash2 size={12} /> Remove
                  </button>
                )}
              </div>
            </div>
          ))}
          {status === "ready" && !files.length && (
            <div data-testid="careers-admin-empty" style={{ fontSize: 13, color: "#78716C" }}>No jobs in the Careers folder yet.</div>
          )}
        </div>
      </div>
    </TiltCard>
  );
}
